import { useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";
import { MAP_KINDS, MAP_KIND_LABELS, type MapKind } from "@etus-mindmap/shared/api";
import { useCreateMap } from "../api/hooks";
import { MAP_TEMPLATES } from "../lib/templates";

// Modal de criação: mostra os 6 tipos com um preview esquemático de cada.
// Clicar cria o mapa (título default "new document") e vai para o editor.

const DESC: Record<MapKind, string> = {
  mindmap: "Ideia central com ramos à direita.",
  "mindmap-bi": "Ramos para os dois lados da raiz.",
  org: "Hierarquia de cima para baixo.",
  timeline: "Marcos numa linha do tempo.",
  matrix: "Quatro quadrantes numa grade 2×2.",
  concept: "Nós livres com conexões rotuladas.",
  flow: "Processos, decisões e setas.",
};

// Previews esquemáticos (viewBox 0 0 100 56). Usam currentColor + o verde.
function KindPreview({ kind }: { kind: MapKind }) {
  const s = { stroke: "currentColor", fill: "none", strokeWidth: 2 } as const;
  const box = (x: number, y: number, w = 22, h = 12, fill = "currentColor") => (
    <rect x={x} y={y} width={w} height={h} rx={3} fill={fill} opacity={0.15} stroke="currentColor" strokeWidth={1.5} />
  );
  const svg = (children: React.ReactNode) => (
    <svg viewBox="0 0 100 56" className="h-16 w-full text-primary dark:text-primary-light" aria-hidden>
      {children}
    </svg>
  );

  switch (kind) {
    case "mindmap":
      return svg(
        <>
          <path d="M32 28 H50 M32 28 C 44 12, 56 12, 68 12 M32 28 C 44 44, 56 44, 68 44" {...s} strokeWidth={1.5} />
          {box(10, 22)}
          {box(66, 6, 24, 10)}
          {box(66, 22, 24, 10)}
          {box(66, 38, 24, 10)}
        </>,
      );
    case "mindmap-bi":
      return svg(
        <>
          <path d="M50 28 C 40 14, 26 14, 14 14 M50 28 C 60 14, 74 14, 86 14 M50 28 C 40 42, 26 42, 14 42 M50 28 C 60 42, 74 42, 86 42" {...s} strokeWidth={1.5} />
          {box(39, 22)}
          {box(2, 8, 20, 10)} {box(78, 8, 20, 10)} {box(2, 36, 20, 10)} {box(78, 36, 20, 10)}
        </>,
      );
    case "org":
      return svg(
        <>
          <path d="M50 16 V26 M22 26 H78 M22 26 V36 M50 26 V36 M78 26 V36" {...s} strokeWidth={1.5} />
          {box(39, 4)}
          {box(11, 38)} {box(39, 38)} {box(67, 38)}
        </>,
      );
    case "timeline":
      return svg(
        <>
          <path d="M8 28 H92" {...s} strokeWidth={1.5} />
          {[20, 44, 68].map((x) => (
            <circle key={x} cx={x} cy={28} r={4} fill="currentColor" />
          ))}
          {box(12, 8, 20, 9)} {box(36, 36, 20, 9)} {box(60, 8, 20, 9)}
        </>,
      );
    case "matrix":
      return svg(
        <>
          <path d="M50 6 V50 M8 28 H92" {...s} strokeWidth={1.5} />
          {box(12, 10, 30, 12)} {box(58, 10, 30, 12)} {box(12, 34, 30, 12)} {box(58, 34, 30, 12)}
        </>,
      );
    case "concept":
      return svg(
        <>
          <path d="M24 14 L52 26 M52 26 L30 42 M52 26 L80 20" {...s} strokeWidth={1.5} />
          {box(10, 8, 18, 10)} {box(42, 20, 20, 12)} {box(72, 12, 18, 10)} {box(20, 38, 18, 10)}
        </>,
      );
    case "flow":
      return svg(
        <>
          <path d="M28 15 H40 M46 26 V32" {...s} strokeWidth={1.5} />
          {box(6, 9, 22, 12)}
          <path d="M46 12 L58 24 L46 36 L34 24 Z" fill="currentColor" opacity={0.15} stroke="currentColor" strokeWidth={1.5} />
          {box(66, 30, 24, 12)}
          <path d="M58 24 H78 V30" {...s} strokeWidth={1.5} />
        </>,
      );
  }
}

export function NewMapModal({ onClose }: { onClose: () => void }) {
  const createMap = useCreateMap();
  const navigate = useNavigate();

  const goToMap = (map: { uuid: string }) => navigate({ to: "/maps/$uuid", params: { uuid: map.uuid } });

  const pick = (kind: MapKind) => {
    createMap.mutate({ kind }, { onSuccess: goToMap });
  };

  const pickTemplate = (template: (typeof MAP_TEMPLATES)[number]) => {
    createMap.mutate(
      { kind: template.kind, title: template.title, doc: template.build() },
      { onSuccess: goToMap },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 dark:bg-black/60">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-4 flex flex-shrink-0 items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Novo mapa</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-7 w-7 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="-mr-2 flex-1 overflow-y-auto pr-2">
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Escolha um tipo — o mapa é criado como “new document” e você renomeia no editor.
        </p>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Começar em branco
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {MAP_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              disabled={createMap.isPending}
              onClick={() => pick(k)}
              className="group flex flex-col rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-50 dark:border-slate-800 dark:hover:border-primary"
            >
              <div className="mb-2 rounded-md bg-slate-50 p-1 dark:bg-slate-800/50">
                <KindPreview kind={k} />
              </div>
              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{MAP_KIND_LABELS[k]}</span>
              <span className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{DESC[k]}</span>
            </button>
          ))}
        </div>
        <h3 className="mb-2 mt-5 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Começar de um template
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {MAP_TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={createMap.isPending}
              onClick={() => pickTemplate(t)}
              className="group flex flex-col rounded-lg border border-slate-200 p-3 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-50 dark:border-slate-800 dark:hover:border-primary"
            >
              <div className="mb-2 rounded-md bg-slate-50 p-1 dark:bg-slate-800/50">
                <KindPreview kind={t.kind} />
              </div>
              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{t.name}</span>
              <span className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{t.description}</span>
            </button>
          ))}
        </div>
        {createMap.isError && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">
            Erro ao criar: {(createMap.error as Error).message}
          </p>
        )}
        </div>
      </div>
    </div>
  );
}
